"use client"

import { useEffect, useRef, useState } from "react"
import { ScrollArea } from "@ui/shad"
import { Loader2 } from "lucide-react"

import { useReplacePlaceholders } from "@/hooks/api/email-templates"
import { Editor } from "@/components/lexical"

import { BulkEmailRecipientData } from "./recipient-item"

interface BulkEmailPreviewProps {
  recipient: BulkEmailRecipientData
  subject: string
  body: string
}

export function BulkEmailPreview({ recipient, subject, body }: BulkEmailPreviewProps) {
  const { mutateAsync } = useReplacePlaceholders()
  const [renderedSubject, setRenderedSubject] = useState<string | null>(null)
  const [renderedBody, setRenderedBody] = useState<string | null>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const mutateRef = useRef(mutateAsync)
  mutateRef.current = mutateAsync

  useEffect(() => {
    // Reset to loading state when inputs change
    setRenderedSubject(null)
    setRenderedBody(null)

    // Only support households for now
    if (recipient.type !== "household") {
      setRenderedSubject(subject)
      setRenderedBody(body)
      return
    }

    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    // Debounce the render request to avoid spamming the API while typing
    debounceRef.current = setTimeout(async () => {
      try {
        const [subjectResult, bodyResult] = await Promise.all([
          mutateRef.current({ content: subject, subjectType: "Household", subjectId: recipient.id }),
          mutateRef.current({ content: body, subjectType: "Household", subjectId: recipient.id }),
        ])

        setRenderedSubject(subjectResult.content)
        setRenderedBody(bodyResult.content)
      } catch {
        // On error, show raw content
        setRenderedSubject(subject)
        setRenderedBody(body)
      }
    }, 300)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [subject, body, recipient.id, recipient.type])

  if (renderedSubject === null || renderedBody === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      </div>
    )
  }

  return (
    <ScrollArea className="h-full">
      <div className="space-y-4 p-4">
        <div>
          <p className="text-muted-foreground mb-1 text-xs font-medium uppercase">To</p>
          <p className="text-foreground text-sm">{recipient.email || recipient.name}</p>
        </div>
        <div>
          <p className="text-muted-foreground mb-1 text-xs font-medium uppercase">Subject</p>
          <p className="text-foreground text-sm font-medium">{renderedSubject || "(No subject)"}</p>
        </div>
        <div>
          <p className="text-muted-foreground mb-1 text-xs font-medium uppercase">Body</p>
          <Editor
            initialContent={{ html: renderedBody || "" }}
            editable={false}
            proseClasses="prose-email dark:prose-invert"
            styleOverrides={{
              "--lexical-editor-font-size": "12pt",
            }}
          />
        </div>
      </div>
    </ScrollArea>
  )
}
